import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  handleFetchTemplate,
  handleDeleteTemplate,
} from "../../modules/templateModules.ts";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmModal.tsx";
import Button from "../../components/common/Button.tsx";
import ReusableTable from "../../components/common/Table.tsx";
import {
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "material-react-table";
import {toast, ToastContainer} from 'react-toastify';import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { RiAddLine, RiDeleteBin5Line, RiEditBoxLine } from "react-icons/ri";
import { MoreHoriz, MoreVert } from "@mui/icons-material";

interface Template {
  id: number;
  template_name: string;
  no_of_columns: number;
  created_at: string;
  tenant_id?: string;
  created_by?: number;
  modified_at?: string | null;
  modified_by?: number | null;
}

const TemplateList = () => {
  const navigate = useNavigate();
  //const toast = useToast();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [menuAnchor, setMenuAnchor] = useState<{
    [key: number]: HTMLElement | null;
  }>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Template | null>(null);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-CA");
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const skip = pagination.pageIndex * pagination.pageSize;
        const limit = pagination.pageSize;
        const page = pagination.pageIndex + 1;

        const response = await handleFetchTemplate(skip, limit, page);
        let templatesData: Template[] = [];
        let totalCount = 0;

        if (Array.isArray(response)) {
          templatesData = response;
          totalCount = response.length;
        } else if (
          response &&
          typeof response === "object" &&
          "data" in response &&
          "total" in response
        ) {
          templatesData = response.data;
          totalCount = response.total;
        }

        const formattedData = templatesData.map((template: Template) => ({
          ...template,
          created_at: formatDate(template.created_at),
        }));

        setTemplates(formattedData);
        setTotalRows(totalCount);
      } catch (error) {
        console.error("Error fetching templates:", error);
        setError("Failed to fetch templates.");
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates()
  }, []);

  const handleCreate = () => navigate("/CreateTemplate");

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number
  ) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id: number) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: null }));
  };

  const handleDeleteClick = (template: Template) => {
    setItemToDelete(template);
    setIsDeleteModalOpen(true);
    handleMenuClose(template.id);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      await handleDeleteTemplate(itemToDelete.id);

      const skip = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;
      const page = pagination.pageIndex + 1;
      const response = await handleFetchTemplate(skip, limit, page);
      let templatesData: Template[] = [];
      let totalCount = 0;

      if (Array.isArray(response)) {
        templatesData = response;
        totalCount = response.length;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "total" in response
      ) {
        templatesData = response.data;
        totalCount = response.total;
      }

      const formattedData = templatesData.map((template: Template) => ({
        ...template,
        created_at: formatDate(template.created_at),
      }));

      setTemplates(formattedData);
      setTotalRows(totalCount);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success("Template deleted successfully");
    } catch (error: any) {
      setIsDeleteModalOpen(false);
      toast.error(
        `Failed to delete ${itemToDelete.template_name}. ${error.message}`
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = async (id: number, template_name: string) => {
    navigate(`/Edit-Template/${id}`, {
      state: {
        template_name: template_name
      }
    });
  };

  const columns = useMemo<MRT_ColumnDef<Template>[]>(
    () => [
      {
        accessorKey: "actions",
        header: " ",
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        size: 10,
        Cell: ({ row }) => (
          <>
            <IconButton onClick={(e) => handleMenuClick(e, row.original.id)}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={menuAnchor[row.original.id]}
              open={Boolean(menuAnchor[row.original.id])}
              onClose={() => handleMenuClose(row.original.id)}
              sx={{
                "& .MuiPaper-root": {
                  backgroundColor: "#fff",
                  width: "105px",
                },
              }}
            >
              <MenuItem
                onClick={() => handleEdit(row.original.id, row.original.template_name)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiEditBoxLine size={18} />&nbsp;&nbsp;Edit
              </MenuItem>
              <MenuItem
                onClick={() => handleDeleteClick(row.original)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiDeleteBin5Line size={18} />&nbsp;&nbsp;Delete
              </MenuItem>
            </Menu>
          </>
        ),
      },
      { accessorKey: "template_name", header: "Template Name", size: 150 },
      { accessorKey: "no_of_columns", header: "No. of Headers", size: 150 },
      { accessorKey: "created_at", header: "Date Created", size: 150 },
    ],
    [menuAnchor]
  );

  useEffect(() => {
    const showToast = sessionStorage.getItem("LoginToast");

    if (showToast === "True") {
      setTimeout(() => {
        toast.success("Login Successful! Welcome back!", {
          toastId: "login-success",
        });
        sessionStorage.removeItem("LoginToast");
      }, 100);
    }
  }, []);

  return (
    <>
      <BreadCrumb moduleName={"Template"} pageName={"Template List"} />
      <ToastContainer position="top-center" />
      <div className="d-flex flex-sm-row flex-column w-100 justify-content-between align-items-lg-center gap-2 mb-1 mt-0">
        <div className="d-flex flex-row gap-2">
          <h5 className="rv-page-title p-2">Templates List</h5>
        </div>
        <div className="d-flex align-items-center">
          <button
            type="button"
            onClick={handleCreate}
            className="btn-label rv-primary-btn me-2 rounded-1 px-4 py-1 text-nowrap btn btn-secondary"
          >
            <i className="ri-add-line label-icon align-middle fs-16 me-1"></i>
            <span className="ps-3 ms-2 me-1">Add Template</span>
          </button>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemType="Template"
        itemName={itemToDelete?.template_name}
        loading={deleteLoading}
      />

      <div className="container-fluid px-0">
        <div className="disable-margin-card table-responsive table-customer border table-border w-100">
          <ReusableTable
            columns={columns}
            data={templates}
            loading={loading}
            error={error}
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </div>
      </div>
    </>
  );
};

export default TemplateList;
